import { DictionaryEntry } from '@sdbn/common';
import { MasterDetailListEntry, TreeListNode, TreeListEntry } from '@sdbn/common';
import { DateTime } from 'luxon';
import { Description, Period, RetailPrice } from 'src/erp-articles/erp-common/erp-common.model';
import { BehaviorSubject } from "rxjs";

export class ProjectAdminContext {

  selectedProjects = new Array<number>();
  currentlyActiveProject: number;
  widgetIdentifier: string;

  public contextReady = new BehaviorSubject<void>(null);

}

export class ProjectColumnDefinitions {

  // Datenstruktur wird exklusiv im Client genutzt.
  definitions = new Map<string, string[]>();

  constructor(d: any) {
    if (d.definitions != null) {
      for (const k of Object.keys(d.definitions)) {
        this.definitions.set(k, d.definitions[k]);
      }
    }
  }
}

export class ProjectTableGroupHeader {
  level: number;
  name: DictionaryEntry;
  projectPart: ProjectPart;

  constructor(header: any) {
    this.level = header?.level;
    this.name = header?.name;
    this.projectPart = header?.projectPart;
  }
}

export class ProjectSearchResultEntry {
  id: number;
  name: string;
  nameDictionaryKey: string;
  startDate: string;
  endDate: string;
  archived: boolean;

  constructor(project: any) {
    this.id = project.id;
    this.name = project.name;
    this.startDate = project.startDate;
    this.endDate = project.endDate;
    this.archived = project.archived;
  }
}

export class Project {

  id: number;
  name: string;
  validPeriod: Period;
  archived: boolean;
  parts = new Array<ProjectPart>();

  constructor(project: any) {
    this.id = project.id;
    this.name = project.name;
    if (project.validPeriod != null) {
      this.validPeriod = new Period(project.validPeriod);
    }
    this.archived = project.archived;
    if (project.parts != null) {
      for (const part of project.parts) {
        this.parts.push(new ProjectPart(part));
      }
    }
  }
}

export class ProjectPart implements TreeListNode {

  id: number;
  name: DictionaryEntry;
  nameDictionaryKey: string;

  parts = new Array<ProjectPart>();
  positions = new Array<ProjectPosition>();

  constructor(part: any) {
    this.id = part.id;
    this.name = part.name;
    if (part.parts != null) {
      for (let child of part.parts) {
        this.parts.push(new ProjectPart(child));
      }
    }
    if (part.positions != null) {
      for (let entry of part.positions) {
        const position = new ProjectPosition(entry);
        this.positions.push(position);
        position.projectPartId = this.id;
      }
    }
  }

  public get childs(): Array<TreeListNode> {
    return this.parts;
  };

  public get entries(): Array<TreeListEntry> {
    return this.positions;
  }

}

export class ProjectPositionAttribute {

  attributeId: number;
  name = new DictionaryEntry();
  value: string;

  constructor(entry: any) {
    this.attributeId = entry.attributeId;
    this.value = entry.value;
    if (entry.name != null) {
      this.name = new DictionaryEntry(entry.name);
    }
  }
}

export class ProjectPosition {

  id: number;
  name = new Array<Description>();
  description: string;
  amount: number;
  priceNet: number;
  priceFixed: number;
  sumNet: number;
  referenceDate: Date;
  articleId: number;
  articleTypeId: number;
  articleIdentifier: string;
  calculationMode: ProjectPositonCalculationMode;
  price: RetailPrice;
  costsPriceSource: string;
  notBillingRelevant: boolean;
  projectPositionAttributes = new Array<ProjectPositionAttribute>();

  projectPartId: number;

  // Datenstruktur wird exklusiv im Client genutzt.
  valuesMap = new Map<string, string>();
  // Schnittstellenobject zum Server
  values: any = {};

  constructor(entry: any) {
    this.id = entry.id;
    if (entry.name != null) {
      for (const item of entry.name) {
        this.name.push(new Description(item));
      }
    }
    this.description = entry.description;
    this.amount = entry.amount;
    this.priceNet = entry.priceNet;
    this.priceFixed = entry.priceFixed;
    this.sumNet = entry.sumNet;
    this.articleId = entry.articleId;
    this.articleTypeId = entry.articleTypeId;
    this.articleIdentifier = entry.articleIdentifier;
    this.calculationMode = entry.calculationMode;
    this.costsPriceSource = entry.costsPriceSource;
    this.notBillingRelevant = entry.notBillingRelevant;
    if (entry.price == null) {
      this.price = new RetailPrice({});
    } else {
      this.price = new RetailPrice(entry.price);
    }
    if (entry.referenceDate != null) {
      if (entry.referenceDate instanceof Date) {
        this.referenceDate = entry.referenceDate;
      } else {
        const date = DateTime.fromISO(entry.referenceDate);
        if (date.isValid) {
          this.referenceDate = new Date(date.year, date.month - 1, date.day);
        }
      }
    }
    if (entry.projectPositionAttributes != null) {
      for (const k of entry.projectPositionAttributes) {
        this.projectPositionAttributes.push(new ProjectPositionAttribute(k));
      }
    }
    if (entry.values != null) {
      for (const k of Object.keys(entry.values)) {
        this.values[k] = entry.values[k];
        this.valuesMap.set(k, entry.values[k]);
      }
    }
  }

  public getValueByAttributeId(attributeId: number): string {
    return this.valuesMap.get(attributeId + '');
  }

  /**
   * Befüllt das Attribut 'values' der Serverschnittstelle basierend auf der im Client
   * verwendeten Map 'valuesMap'.
   */
  public fillValuesFromValuesMap() {
    this.values = {};
    this.valuesMap.forEach((val: string, key: string) => {
      this.values[key] = val;
    });
  }

}

export class AddProject {

  name: string;
  startDate: Date;
  endDate: Date;

  constructor(addProject: any) {
    this.name = addProject.name;
    this.startDate = addProject.startDate;
    this.endDate = addProject.endDate;
  }

}

export interface ChangeProject {
  projectId: number;
  name: string;
  startDate: Date;
  endDate: Date;
  archived: boolean;
}

export class ChangeProjectPart {
  projectId: number;
  projectPartId: number;
  name: string;

  constructor(project: any) {
    this.projectId = project.projectId;
    this.projectPartId = project.projectPartId;
    this.name = project.name;
  }
}

export class AddProjectPart {

  parentPartId: number;
  projectId: number;

  name: string;

  constructor(addprojectPart: any) {
    this.parentPartId = addprojectPart.parentPartId;
    this.projectId = addprojectPart.projectId;
    this.name = addprojectPart.name;
  }

}

export class SearchArticleResult {

  id: number;
  articleTypeId: number;
  identifier: string;

  constructor(result: any) {
    this.id = result.id;
    this.articleTypeId = result.articleTypeId;
    this.identifier = result.identifier;
  }

}

export class SearchArticle {

  query: string;

  constructor(search: any) {
    this.query = search.query;
  }

}

export class AddProjectPosition {

  projectPartId: number;
  referenceDate: Date;
  articleId: number;
  amount: number;
  calculationMode: ProjectPositonCalculationMode;
  price: RetailPrice;
  costsPriceSource: string;
  // Schnittstellenobject zum Server
  values: any = {};
  notBillingRelevant: boolean;

  constructor(addProjectPosition: any) {
    this.projectPartId = addProjectPosition.projectPartId;
    this.referenceDate = addProjectPosition.referenceDate;
    this.articleId = addProjectPosition.articleId;
    this.amount = addProjectPosition.amount;
    this.calculationMode = addProjectPosition.calculationMode;
    this.price = addProjectPosition.price;
    this.costsPriceSource = addProjectPosition.costsPriceSource;
    this.values = addProjectPosition.values;
    this.notBillingRelevant = addProjectPosition.notBillingRelevant;
  }

}

export class ProjectSelection {

  name: string;
  startDate: Date;
  endDate: Date;
  archived: boolean;

  constructor(addProject: any) {
    this.name = addProject.name;
    this.startDate = addProject.startDate;
    this.endDate = addProject.endDate;
    this.archived = addProject.archived;
  }
}

export class ProjectTitle {
  id: number;
  title: string;
  constructor(projectTitle: any) {
    this.id = projectTitle.id;
    this.title = projectTitle.title;
  }
}

export enum ProjectPositonCalculationMode {
  ARTICLE_BASED = 'ARTICLE_BASED', MANUAL_AMOUNT = 'MANUAL_AMOUNT', MANUAL = 'MANUAL', FIXED = "FIXED"
}


